export const formatDateTime = (
  isoString: string,
  options?: {
    withTime?: boolean
    timezone?: 'local' | 'utc'
    separator?: string
  },
): string => {
  const {
    withTime = true,
    timezone = 'local',
    separator = ', ',
  } = options || {}

  try {
    const date = new Date(isoString)

    if (Number.isNaN(date.getTime())) {
      return 'Неверная дата'
    }

    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()

    let formattedDate = `${day}.${month}.${year}`

    if (withTime) {
      let hours: number
      let minutes: number
      let seconds: number

      if (timezone === 'local') {
        hours = date.getHours()
        minutes = date.getMinutes()
        seconds = date.getSeconds()
      } else {
        hours = date.getUTCHours()
        minutes = date.getUTCMinutes()
        seconds = date.getUTCSeconds()
      }

      const formattedHours = String(hours).padStart(2, '0')
      const formattedMinutes = String(minutes).padStart(2, '0')
      const formattedSeconds = String(seconds).padStart(2, '0')

      formattedDate += `${separator}${formattedHours}:${formattedMinutes}:${formattedSeconds}`
    }

    return formattedDate
  } catch (error) {
    return 'Ошибка формата'
  }
}

const getNumericDateParts = (
  value: string,
  separator: string,
  partLengths: number[],
) => {
  const parts = value.split(separator)
  const hasExpectedFormat =
    parts.length === partLengths.length &&
    parts.every(
      (part, index) =>
        part.length === partLengths[index] &&
        [...part].every((character) => character >= '0' && character <= '9'),
    )

  return hasExpectedFormat ? parts.map(Number) : null
}

const parseDateValue = (value: string | Date) => {
  if (value instanceof Date) {
    return value
  }

  const dotSeparatedParts = getNumericDateParts(value, '.', [2, 2, 4])

  if (dotSeparatedParts) {
    const [day, month, year] = dotSeparatedParts
    return new Date(year, month - 1, day)
  }

  const dashSeparatedParts = getNumericDateParts(value, '-', [4, 2, 2])

  if (dashSeparatedParts) {
    const [year, month, day] = dashSeparatedParts
    return new Date(year, month - 1, day)
  }

  return new Date(value)
}

export const formatDate = (value: string | Date | null | undefined): string => {
  if (!value) {
    return ''
  }

  if (typeof value === 'string' && getNumericDateParts(value, '.', [2, 2, 4])) {
    return value
  }

  const date = parseDateValue(value)

  if (Number.isNaN(date.getTime())) {
    return String(value)
  }

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()

  return `${day}.${month}.${year}`
}

export const formatTime = (value: string | Date | null | undefined): string => {
  if (!value) {
    return ''
  }

  const date = parseDateValue(value)

  if (Number.isNaN(date.getTime())) {
    return String(value)
  }

  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return `${hours}:${minutes}`
}

export const formatLongDate = (
  value: string | Date | null | undefined,
  locale = 'ru',
): string => {
  if (!value) {
    return ''
  }

  const date = parseDateValue(value)

  if (Number.isNaN(date.getTime())) {
    return String(value)
  }

  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
  }).format(date)
}

export const formatDateRange = (value: string | null | undefined): string => {
  if (!value) {
    return ''
  }

  const parts = value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)

  if (parts.length === 2) {
    return `${formatDate(parts[0])} - ${formatDate(parts[1])}`
  }

  return formatDate(value)
}
